import React from 'react';
import { 
  XMarkIcon, 
  TrophyIcon, 
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon 
} from '@heroicons/react/24/outline';

import Button from '../Button';

const ClaimRewardModal = ({ 
  isOpen, 
  onClose, 
  rewardData, 
  onClaim, 
  isLoading = false 
}) => {
  // console.log({rewardData});

  if (!isOpen || !rewardData) return null;

  const handleClaim = () => {
    if (onClaim && !isLoading) {
      onClaim(rewardData.id);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-50 backdrop-blur-sm overflow-hidden"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-2xl max-w-sm sm:max-w-md w-full mx-2 sm:mx-4 border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="relative p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="flex-shrink-0 p-2 sm:p-3 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-lg sm:rounded-xl">
              <TrophyIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                Claim Your Reward
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Congratulations on your victory!
              </p>
            </div>
          </div>
          
          {/* Close button */}
          {!isLoading && (
            <button
              onClick={onClose}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Game Information */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg sm:rounded-xl p-3 sm:p-4 space-y-2 sm:space-y-3">
            <h4 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">Game Details</h4>
            
            <div className="grid grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Room ID:</span>
                <p className="font-mono text-xs sm:text-sm text-gray-900 dark:text-white">
                  {rewardData.roomId || 'N/A'}
                </p>
              </div>
              
              <div>
                <span className="text-gray-500 dark:text-gray-400">Game Date:</span>
                <p className="text-xs sm:text-sm text-gray-900 dark:text-white">
                  {rewardData.endTime ? new Date(rewardData.endTime).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              
              <div>
                <span className="text-gray-500 dark:text-gray-400">Your Score:</span>
                <p className="text-xs sm:text-sm text-gray-900 dark:text-white font-semibold">
                  {rewardData.player?.score || 0}
                </p>
              </div>
              
              <div>
                <span className="text-gray-500 dark:text-gray-400">Result:</span>
                <span className="inline-flex px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                  VICTORY
                </span>
              </div>
            </div>
          </div>

          {/* Reward Information */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-yellow-200 dark:border-yellow-700">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
              <CurrencyDollarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600 dark:text-yellow-400" />
              <h4 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">Reward Amount</h4>
            </div>
            
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                100 TOKENS
              </div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                Winner's reward for this game
              </p>
            </div>
          </div>

          {/* Winners List */}
          {rewardData.winners && rewardData.winners.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-blue-200 dark:border-blue-700">
              <h4 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white mb-2 sm:mb-3">
                {rewardData.winners.length > 1 ? 'Co-Winners' : 'Winner'}
              </h4>
              <div className="space-y-1.5 sm:space-y-2">
                {rewardData.winners.map((winner, index) => (
                  <div key={index} className="flex items-center space-x-1.5 sm:space-x-2">
                    <CheckCircleIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500" />
                    <span className="text-xs sm:text-sm text-gray-900 dark:text-white">
                      {winner.username || `${winner.address?.slice(0, 6)}...${winner.address?.slice(-4)}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Claim Status */}
          <div className="flex items-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            <ClockIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span>
              Claim your reward to transfer tokens to your wallet
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 grid grid-cols-2 gap-3 justify-between sm:p-6 border-t border-gray-200 w-full dark:border-gray-700 ">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
            className="bg-gradient-to-r from-gray-950 to-gray-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-bold py-2.5 w-full px-4 sm:px-6 sm:py-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base"
          >
            Cancel
          </Button>
          
          <Button
            onClick={handleClaim}
            disabled={isLoading}
            className='bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-bold py-3 sm:py-3 px-3 sm:px-4 lg:px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex w-full items-center justify-center gap-2 text-xs sm:text-sm lg:text-base'
          >
            {isLoading ? (
              <div className="flex items-center py-3 space-x-1.5 sm:space-x-2">
                <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-b-2 border-white"></div>
                <span>Claiming...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <TrophyIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>Claim Reward</span>
              </div>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClaimRewardModal;